import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { ComponentProps, useId, useMemo, useState } from 'react';
import type { Options } from 'react-markdown';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { getNavigationDisplayName, NavigationEntityType } from '../../../../models/hooks/useAINavigationResolver';
import { useEventLogging } from '../../../../models/Logging';
import {
	getConversationChatMessageQueryKey,
	updateMessageInConversationCache,
} from '../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { useAIConversationMessageFeedbackMutation } from '../../../../queries/AIConversation/useAIConversationMessageFeedbackMutation';
import { useDeleteAIConversationMessageFeedbackMutation } from '../../../../queries/AIConversation/useDeleteAIConversationMessageFeedbackMutation';
import { AIConversationMessageAttachments } from './AIConversationMessageAttachments';
import { ConfirmationCard, type ConfirmationResponsePayload } from './ConfirmationCard';
import { ThumbsDownIcon } from '../../../components/svgs/icons/ThumbsDownIcon';
import { ThumbsUpIcon } from '../../../components/svgs/icons/ThumbsUpIcon';
import moment from 'moment';
import { EmailIcon } from '../../../components/svgs/icons/EmailIcon';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/Tooltip';
import {
	AIChatAssistantMessage,
	AIChatAssistantMessageError,
	AIChatMessageError,
	AIChatUserMessage,
} from '../../../containers/templates/AssistantChat';
import { AssistantChatContactCard } from '../../../containers/templates/AssistantChat/AssistantChatContactCard';
import { AIChatMessageWithProgress, CLIENT_MESSAGE_ID_PREFIX } from '../../../containers/templates/AssistantChat/utils';
import { darkGrayFontColor, white } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { Button } from '../../Button';
import { Checkbox } from '../../Checkbox';
import { THEME } from '../../Theme/theme';
import { MediaContentDisplay } from '../MediaContentDisplay';
import { AIMarkdownResponse } from '../AiMarkdownResponse';
import { ThinkingIndicator } from '../ThinkingIndicator';
import type { TextRenderer } from './renderers';
import { BlogPostIcon } from '../../../components/svgs/icons/BlogPostIcon';
import { EyeIcon } from '../../../components/svgs/icons/EyeIcon';
import { HeartAndShareIcon } from '../../../components/svgs/icons/HeartAndShareIcon';

interface PartialToolCall extends Api.AIToolCall {
	partial: Record<string, string>;
}

function isEmailToolCall(toolCall: Api.AIToolCall): toolCall is Api.EmailToolCall {
	return Boolean((toolCall as Api.EmailToolCall)?.body);
}

function isSocialToolCall(toolCall: Api.AIToolCall): toolCall is Api.SocialMediaToolCall {
	return Boolean((toolCall as Api.SocialMediaToolCall)?.content);
}

function isGoogleReviewReplyToolCall(toolCall: Api.AIToolCall): toolCall is Api.GoogleReviewReplyToolCall {
	return Boolean((toolCall as Api.GoogleReviewReplyToolCall)?.reply);
}

function isBlogToolCall(toolCall: Api.AIToolCall): toolCall is Api.BlogToolCall {
	return Boolean((toolCall as Api.BlogToolCall)?.title);
}

function isContactListToolCall(toolCall: Api.AIToolCall): toolCall is Api.ContactListToolCall {
	return Boolean((toolCall as Api.ContactListToolCall)?.toolName === 'lev-contacts');
}

function isNavigationToolCall(toolCall: Api.AIToolCall): toolCall is Api.NavigationToolCall {
	return Boolean((toolCall as Api.NavigationToolCall)?.toolName === 'lev-navigation');
}

function isMediaContentToolCall(toolCall: Api.AIToolCall): toolCall is Api.MediaContentToolCall {
	return toolCall.toolName === 'lev-media-content';
}

function isMeetingSchedulingToolCall(toolCall: Api.AIToolCall): toolCall is Api.MeetingSchedulingToolCall {
	return toolCall.toolName === 'lev-meeting-scheduling';
}

function isConfirmationToolCall(toolCall: Api.AIToolCall): toolCall is Api.ConfirmationToolCall {
	return toolCall.toolName === 'lev-confirmation';
}

function emailToolCallPreviewContent(toolCall: PartialToolCall) {
	let content: { subject: string; body: string };
	if (isEmailToolCall(toolCall)) {
		content = JSON.parse(toolCall.value);
	} else if (toolCall.partial) {
		content = toolCall.partial as typeof content;
	}
	return content;
}

function socialToolCallPreviewContent(toolCall: PartialToolCall) {
	let content: { content: string };
	if (isSocialToolCall(toolCall)) {
		content = JSON.parse(toolCall.value);
	} else if (toolCall.partial) {
		content = toolCall.partial as typeof content;
	}
	return content;
}

function googleReviewToolCallPreviewContent(toolCall: PartialToolCall) {
	let content: { reply: string };
	if (isGoogleReviewReplyToolCall(toolCall)) {
		content = { reply: toolCall.reply };
	} else if (toolCall.partial) {
		content = { reply: (toolCall.partial as { body: string }).body };
	}
	return content;
}

function blogToolCallPreviewContent(toolCall: PartialToolCall) {
	let content: { title: string; body: string };
	if (isBlogToolCall(toolCall)) {
		content = JSON.parse(toolCall.value);
	} else if (toolCall.partial) {
		content = toolCall.partial as typeof content;
	}
	return content;
}

// Message Action Components
export type MessageActionsProps = ComponentProps<'div'> & {
	styles?: any[];
};

export const MessageActions = ({ className, children, styles = [], ...props }: MessageActionsProps) => (
	<div className={css(s.messageActions, ...styles)} {...props}>
		{children}
	</div>
);

export type MessageActionProps = ComponentProps<typeof Button> & {
	tooltip?: string;
	label?: string;
};

export const MessageAction = ({
	tooltip,
	children,
	label,
	variant = 'ghost',
	size = 'sm',
	...props
}: MessageActionProps) => {
	const button = (
		<Button size={size} variant={variant} {...props}>
			{children}
			<span className={css(s.srOnly)}>{label || tooltip}</span>
		</Button>
	);

	if (tooltip) {
		return (
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>{button}</TooltipTrigger>
					<TooltipContent>
						<p className={css(s.tooltipText)}>{tooltip}</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		);
	}

	return button;
};

export type MessageToolbarProps = ComponentProps<'div'> & {
	styles?: any[];
};

export const MessageToolbar = ({ className, children, styles = [], ...props }: MessageToolbarProps) => (
	<div className={css(s.messageToolbar, ...styles)} {...props}>
		{children}
	</div>
);

interface Props {
	message: Api.AIChatMessage;
	conversationId: string;
	impersonationContext?: Api.IImpersonationContext;
	onUseAIEmail?: (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => void;
	onUseContact?: (value: Api.AIContactReference) => void;
	onUseContactList?: (contactList: Api.ContactListToolCall) => void;
	onUseSocialContent?: (content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseGoogleReviewReply?: (reply: string, sessionId?: string) => void;
	onUseBlogContent?: (subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onNavigate?: (entityType: string, entityId: string, parentEntityType?: string, parentEntityId?: string) => void;
	onErrorTryAgain?: (chatRequest: Api.AIChatRequest) => void;
	onUseImage?: (fileAttachments: Api.IFileAttachment[]) => void;
	onImageGenJobComplete?: () => void;
	showMessageToolbar?: boolean;
	onApproveContentCalendar?: (toolCall: Api.ContentCalendarResourceToolCall, selectedEntryIds?: string[]) => void;
	onApproveContentCalendarLoading?: boolean;
	/** Entry IDs already scheduled via "Use this Content" — approval is additive, so these render checked and read-only */
	approvedContentCalendarEntryIds?: string[];
	renderMeetingSchedulingWidget?: (toolCall: Api.MeetingSchedulingToolCall) => React.ReactNode;
	/** Custom text renderers for transforming message content */
	customRenderers?: TextRenderer[];
	onPreviewCalendarEntry?: (entry: Api.ContentCalendarEntryToolCall) => void;
	onConfirmationResponse?: (args: {
		messageId: string;
		toolCall: Api.ConfirmationToolCall;
		response: ConfirmationResponsePayload;
	}) => void;
	confirmationResponseInFlight?: boolean;
	/** Host-computed via getLatestConfirmationToolCallId — older confirmation cards render superseded */
	latestConfirmationToolCallId?: string;
}

function groupEntriesByMonth(
	entries: Api.ContentCalendarEntryToolCall[]
): [string, Api.ContentCalendarEntryToolCall[]][] {
	const sorted = [...entries].sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
	const groups = new Map<string, Api.ContentCalendarEntryToolCall[]>();
	for (const entry of sorted) {
		const key = moment(entry.scheduledDate).format('MMMM YYYY');
		const existing = groups.get(key);
		if (existing) {
			existing.push(entry);
		} else {
			groups.set(key, [entry]);
		}
	}
	return Array.from(groups.entries());
}

function formatSource(source: string) {
	return source.replace(/([A-Z])/g, ' $1').trim();
}

function ContentTypeIcon({ contentType }: { contentType: string }) {
	if (contentType === 'Email') {
		return <EmailIcon width={16} height={16} fillColor={THEME.colors.icon.gray} />;
	}
	if (contentType === 'Social' || contentType === 'SocialMediaPost') {
		return <HeartAndShareIcon width={16} height={16} fillColor={THEME.colors.icon.gray} />;
	}
	if (contentType === 'Blog') {
		return <BlogPostIcon width={16} height={16} />;
	}
	return null;
}

export function AIConversationMessage({
	message,
	conversationId,
	impersonationContext,
	onUseAIEmail,
	onUseContact,
	onUseContactList,
	onUseSocialContent,
	onUseGoogleReviewReply,
	onUseBlogContent,
	onNavigate,
	onErrorTryAgain,
	onUseImage,
	onImageGenJobComplete,
	showMessageToolbar = true,
	onApproveContentCalendar,
	onApproveContentCalendarLoading,
	approvedContentCalendarEntryIds,
	renderMeetingSchedulingWidget,
	customRenderers,
	onPreviewCalendarEntry,
	onConfirmationResponse,
	confirmationResponseInFlight,
	latestConfirmationToolCallId,
}: Props) {
	const id = useId();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('AIConversationMessage');

	const rendererConfig = useMemo(() => {
		if (!customRenderers?.length) return null;
		let mergedComponents: Options['components'] = {};
		for (const renderer of customRenderers) {
			if (renderer.components) {
				mergedComponents = { ...mergedComponents, ...renderer.components };
			}
		}
		return {
			components: Object.keys(mergedComponents).length ? mergedComponents : undefined,
			preprocess: (text: string) => {
				let result = text;
				for (const renderer of customRenderers) {
					if (renderer.preprocess) {
						result = renderer.preprocess(result);
					}
				}
				return result;
			},
		};
	}, [customRenderers]);
	const shouldShowActions =
		showMessageToolbar && message.role === Api.AIChatRole.Assistant && !message.id.startsWith(CLIENT_MESSAGE_ID_PREFIX);
	const feedbackMutation = useAIConversationMessageFeedbackMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('AIMessageFeedback-Error', error);
		},
	});
	const deleteFeedbackMutation = useDeleteAIConversationMessageFeedbackMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('AIMessageFeedbackDelete-Error', error);
		},
	});

	const calendarEntries = useMemo(
		() =>
			(message.contents ?? [])
				.filter(
					(c): c is Api.AIToolContent =>
						c._type === 'AIToolContent' && (c as Api.AIToolContent).toolCall.toolName === 'lev-content-calendar-entry'
				)
				.map(c => c.toolCall as Api.ContentCalendarEntryToolCall),
		[message.contents]
	);

	const contentsToRender = useMemo(() => {
		let calendarEntrySeen = false;
		return (message.contents ?? []).filter(c => {
			if (c._type === 'AIToolContent' && (c as Api.AIToolContent).toolCall.toolName === 'lev-content-calendar-entry') {
				if (calendarEntrySeen) return false;
				calendarEntrySeen = true;
			}
			return true;
		});
	}, [message.contents]);

	const [manuallyUnchecked, setManuallyUnchecked] = useState<Set<string>>(new Set());
	const selectedEntryIds = useMemo(
		() => new Set(calendarEntries.map(e => e.entryId).filter(entryId => !manuallyUnchecked.has(entryId))),
		[calendarEntries, manuallyUnchecked]
	);
	const approvedEntryIds = useMemo(
		() => new Set(approvedContentCalendarEntryIds ?? []),
		[approvedContentCalendarEntryIds]
	);
	const selectableEntries = calendarEntries.filter(e => !approvedEntryIds.has(e.entryId));
	const selectedIds = selectableEntries.filter(e => selectedEntryIds.has(e.entryId)).map(e => e.entryId);
	const allSelected = selectableEntries.length > 0 && selectedIds.length === selectableEntries.length;
	// The select-all control is gated on the resource tool call so it only appears once streaming has delivered it
	const hasCalendarResource = (message.contents ?? []).some(
		c => c._type === 'AIToolContent' && (c as Api.AIToolContent).toolCall.toolName === 'lev-resource'
	);

	const toggleCalendarEntry = (entryId: string) => {
		setManuallyUnchecked(prev => {
			const next = new Set(prev);
			if (next.has(entryId)) next.delete(entryId);
			else next.add(entryId);
			return next;
		});
	};

	if (message.role === Api.AIChatRole.User) {
		const textFromContents = message.contents
			?.filter(c => c._type === 'AITextContent')
			.map(c => (c as Api.AITextContent).text)
			.join('');
		return (
			<AIChatUserMessage>
				{textFromContents || message.text}
				<AIConversationMessageAttachments contents={message.contents} impersonationContext={impersonationContext} />
				{message.fileAttachments?.length ? (
					<div className={css(bs.flex, bs.gap2, bs.mt2, bs.flexWrap)}>
						{message.fileAttachments.map((attachment, idx) => (
							<img
								key={`${id}-attachment-${message.id}-${idx}`}
								src={attachment.url}
								alt={attachment.fileName}
								className={css(s.attachmentImage)}
							/>
						))}
					</div>
				) : null}
			</AIChatUserMessage>
		);
	}
	if (message.role === Api.AIChatRole.Assistant) {
		const progress = (message as AIChatMessageWithProgress).progress;
		if (progress) {
			return (
				<AIChatAssistantMessage isThinking>
					<ThinkingIndicator messages={[progress.message]} showTime={false} />
				</AIChatAssistantMessage>
			);
		}
		return (
			<AIChatAssistantMessage>
				{contentsToRender.map((content, j) => {
					if (content._type === 'AITextContent') {
						const c = content as Api.AITextContent;
						const processedText = rendererConfig?.preprocess ? rendererConfig.preprocess(c.text) : c.text;
						return (
							<AIMarkdownResponse key={`${id}-assistant-message-${j}`} components={rendererConfig?.components}>
								{processedText}
							</AIMarkdownResponse>
						);
					}
					if (content._type === 'AIToolContent' && (content as Api.AIToolContent).toolCall.toolName === 'lev-email') {
						const partialEmailToolCall = (content as Api.AIToolContent).toolCall as PartialToolCall;
						const previewContent = emailToolCallPreviewContent(partialEmailToolCall);
						return (
							<div key={`${id}-assistant-message-${j}`} className={css(s.assistantEmail)}>
								<div>{previewContent.subject ?? ''}</div>
								<div
									dangerouslySetInnerHTML={{
										__html: previewContent?.body ?? '',
									}}
								/>
								{isEmailToolCall(partialEmailToolCall) ? (
									<Button
										styles={[s.useEmailBtn]}
										onClick={() => {
											onUseAIEmail?.(
												partialEmailToolCall.subject,
												partialEmailToolCall.body,
												partialEmailToolCall.audienceInfo,
												{ sessionId: conversationId }
											);
										}}
									>
										Use this email
									</Button>
								) : null}
							</div>
						);
					}
					if (content._type === 'AIToolContent' && (content as Api.AIToolContent).toolCall.toolName === 'lev-contact') {
						const contactToolContent = (content as Api.AIToolContent).toolCall as unknown as Api.AIContactReference & {
							contactId: string;
						};
						return (
							<AssistantChatContactCard
								key={`contact-assistant-message${message?.id}-${j}`}
								firstName={contactToolContent.firstName}
								lastName={contactToolContent.lastName}
								email={contactToolContent.primaryEmail}
								phone={contactToolContent.phoneNumbers?.[0]?.value}
								company={contactToolContent.companyName}
								profilePic={contactToolContent.profilePic}
								onClick={() => {
									onUseContact?.({
										...contactToolContent,
										id: contactToolContent.contactId,
									});
								}}
							/>
						);
					}
					if (content._type === 'AIToolContent' && isContactListToolCall((content as Api.AIToolContent).toolCall)) {
						const contactListToolCall = (content as Api.AIToolContent).toolCall as Api.ContactListToolCall;
						return (
							<Button
								key={`contact-list-assistant-message${message?.id}-${j}`}
								onClick={() => {
									onUseContactList?.(contactListToolCall);
								}}
							>
								Show all {contactListToolCall.totalCount} contacts
							</Button>
						);
					}
					if (
						content._type === 'AIToolContent' &&
						(content as Api.AIToolContent).toolCall.toolName === 'lev-social-media-post'
					) {
						const partialSocialCall = (content as Api.AIToolContent).toolCall as PartialToolCall;
						const previewContent = socialToolCallPreviewContent(partialSocialCall);
						return (
							<div key={`social-assistant-message${message?.id}-${j}`} className={css(s.assistantEmail)}>
								<div
									dangerouslySetInnerHTML={{
										__html: previewContent?.content ?? '',
									}}
								/>
								{isSocialToolCall(partialSocialCall) ? (
									<Button
										styles={[s.useEmailBtn]}
										onClick={() => onUseSocialContent?.(partialSocialCall.content, { sessionId: conversationId })}
									>
										Use this post
									</Button>
								) : null}
							</div>
						);
					}
					if (
						content._type === 'AIToolContent' &&
						(content as Api.AIToolContent).toolCall.toolName === 'lev-google-review-reply'
					) {
						const partialGoogleReviewReply = (content as Api.AIToolContent).toolCall as PartialToolCall;
						const previewContent = googleReviewToolCallPreviewContent(partialGoogleReviewReply);
						return (
							<div key={`${id}-assistant-message-${j}`} className={css(s.assistantEmail)}>
								<div>{previewContent?.reply ?? ''}</div>
								{onUseGoogleReviewReply && isGoogleReviewReplyToolCall(partialGoogleReviewReply) ? (
									<Button
										styles={[s.useEmailBtn]}
										onClick={() => onUseGoogleReviewReply(partialGoogleReviewReply.reply, message.aiConversationId)}
									>
										Use this Response
									</Button>
								) : null}
							</div>
						);
					}
					if (
						content._type === 'AIToolContent' &&
						(content as Api.AIToolContent).toolCall.toolName === 'lev-content-calendar-entry'
					) {
						const groups = groupEntriesByMonth(calendarEntries);
						return (
							<div key={`${id}-calendar-entries`}>
								{hasCalendarResource && selectableEntries.length > 0 && (
									<div className={css(s.calendarSelectAllRow)}>
										<Button
											variant='link'
											size='sm'
											disabled={Boolean(onApproveContentCalendarLoading)}
											onClick={() =>
												setManuallyUnchecked(allSelected ? new Set(selectableEntries.map(e => e.entryId)) : new Set())
											}
										>
											{allSelected ? 'Deselect all' : 'Select all'}
										</Button>
									</div>
								)}
								{groups.map(([monthLabel, monthEntries], groupIndex) => (
									<div key={monthLabel}>
										<div className={css(s.calendarMonthHeader, groupIndex > 0 && s.calendarMonthHeaderSpaced)}>
											{monthLabel}
										</div>
										{monthEntries.map(entry => (
											<label key={entry.entryId} className={css(s.calendarEntry)}>
												<Checkbox
													styles={[s.calendarEntryCheckbox]}
													checked={approvedEntryIds.has(entry.entryId) || selectedEntryIds.has(entry.entryId)}
													onCheckedChange={() => toggleCalendarEntry(entry.entryId)}
													disabled={Boolean(onApproveContentCalendarLoading) || approvedEntryIds.has(entry.entryId)}
												/>
												<span className={css(s.calendarEntryIcon)}>
													<ContentTypeIcon contentType={entry.contentType} />
												</span>
												<span className={css(s.calendarEntryInfo)}>
													<span className={css(s.calendarEntryText)}>{entry.templateName}</span>
													{entry.reason && <span className={css(s.calendarEntrySource)}>{entry.reason}</span>}
													{entry.source && (
														<span className={css(s.calendarEntrySource)}>Source: {formatSource(entry.source)}</span>
													)}
												</span>
												{entry.templateId && onPreviewCalendarEntry && (
													<MessageAction
														tooltip='Preview'
														styles={[s.calendarEntryPreviewBtn]}
														onClick={e => {
															e.preventDefault();
															onPreviewCalendarEntry(entry);
														}}
													>
														<EyeIcon width={18} height={12} fillColor={THEME.colors.icon.gray} />
													</MessageAction>
												)}
												<span className={css(s.calendarEntryDay)}>{moment(entry.scheduledDate).format('Do')}</span>
											</label>
										))}
									</div>
								))}
							</div>
						);
					}
					if (
						content._type === 'AIToolContent' &&
						(content as Api.AIToolContent).toolCall.toolName === 'lev-resource'
					) {
						const toolCall = (content as Api.AIToolContent).toolCall as unknown as { id: string; resourceId: string };
						return (
							<div key={`${id}-assistant-message-${j}`} className={css(s.assistantEmail)}>
								<Button
									variant='primary'
									styles={[s.useEmailBtn]}
									isLoading={Boolean(onApproveContentCalendarLoading)}
									disabled={
										Boolean(onApproveContentCalendarLoading) || (calendarEntries.length > 0 && selectedIds.length === 0)
									}
									onClick={() =>
										onApproveContentCalendar?.(
											{ id: toolCall.id, resourceId: toolCall.resourceId, calendarId: toolCall.resourceId },
											calendarEntries.length > 0 ? selectedIds : undefined
										)
									}
								>
									Use this Content
								</Button>
							</div>
						);
					}

					if (content._type === 'AIToolContent' && (content as Api.AIToolContent).toolCall.toolName === 'lev-blog') {
						const partialBlog = (content as Api.AIToolContent).toolCall as PartialToolCall;
						const previewContent = blogToolCallPreviewContent(partialBlog);
						return (
							<div key={`${id}-assistant-message-${j}`} className={css(s.assistantEmail)}>
								<div>{previewContent?.title ?? ''}</div>
								<div
									dangerouslySetInnerHTML={{
										__html: previewContent?.body ?? '',
									}}
								/>
								{isBlogToolCall(partialBlog) ? (
									<Button
										variant='primary'
										styles={[s.useEmailBtn]}
										onClick={() =>
											onUseBlogContent?.(partialBlog.title, partialBlog.content, { sessionId: conversationId })
										}
									>
										Use this blog
									</Button>
								) : null}
							</div>
						);
					}
					if (content._type === 'AIToolContent' && isNavigationToolCall((content as Api.AIToolContent).toolCall)) {
						const navigationToolCall = (content as Api.AIToolContent).toolCall as Api.NavigationToolCall;

						return (
							<div key={`navigation-assistant-message${message?.id}-${j}`}>
								{navigationToolCall.navigationItems?.map((navigationItem, navIndex) => {
									const displayText = getNavigationDisplayName(navigationItem.entityType as NavigationEntityType);
									// Extract parent entity information from parentNavigationMetadata
									const parentEntityType = navigationItem.parentNavigationMetadata?.entityType;
									const parentEntityId = navigationItem.parentNavigationMetadata?.id;
									return (
										<Button
											key={`navigation-item-${message?.id}-${j}-${navIndex}`}
											onClick={() => {
												onNavigate?.(navigationItem.entityType, navigationItem.id, parentEntityType, parentEntityId);
											}}
											styles={[s.useEmailBtn]}
										>
											{displayText}
										</Button>
									);
								})}
								{(!navigationToolCall.navigationItems || navigationToolCall.navigationItems.length === 0) && (
									<div key={`navigation-empty-${message?.id}-${j}`}>No navigation items available</div>
								)}
							</div>
						);
					}

					if (content._type === 'AIToolContent' && isMediaContentToolCall((content as Api.AIToolContent).toolCall)) {
						const toolContent = content as Api.AIToolContent;
						return (
							<MediaContentDisplay
								key={`${id}-assistant-message-${j}`}
								toolCall={toolContent.toolCall}
								impersonationContext={impersonationContext}
								onJobComplete={onImageGenJobComplete}
								onUseImage={onUseImage}
							/>
						);
					}
					if (
						content._type === 'AIToolContent' &&
						isMeetingSchedulingToolCall((content as Api.AIToolContent).toolCall) &&
						renderMeetingSchedulingWidget
					) {
						const meetingToolCall = (content as Api.AIToolContent).toolCall as Api.MeetingSchedulingToolCall;
						return renderMeetingSchedulingWidget(meetingToolCall);
					}
					if (content._type === 'AIToolContent' && isConfirmationToolCall((content as Api.AIToolContent).toolCall)) {
						const confirmationToolCall = (content as Api.AIToolContent).toolCall as Api.ConfirmationToolCall;
						const isPersisted = Boolean(message.id) && !message.id.startsWith(CLIENT_MESSAGE_ID_PREFIX);
						const isLatest = !latestConfirmationToolCallId || confirmationToolCall.id === latestConfirmationToolCallId;
						return (
							<ConfirmationCard
								key={`${id}-assistant-message-${j}`}
								toolCall={confirmationToolCall}
								canRespond={Boolean(onConfirmationResponse) && isPersisted && isLatest && !confirmationResponseInFlight}
								isSuperseded={!isLatest}
								onRespond={response =>
									onConfirmationResponse?.({ messageId: message.id, toolCall: confirmationToolCall, response })
								}
							/>
						);
					}
				})}
				{shouldShowActions ? (
					<MessageToolbar>
						<MessageActions>
							<MessageAction
								tooltip={
									message.feedback?.value === Api.AIChatMessageFeedback.ThumbsUp ? 'Remove feedback' : 'Good response'
								}
								variant='ghost'
								styles={[message.feedback?.value === Api.AIChatMessageFeedback.ThumbsUp && s.messageActionToggled]}
								onClick={() => {
									if (feedbackMutation.isPending || deleteFeedbackMutation.isPending) return;
									if (message.feedback?.value === Api.AIChatMessageFeedback.ThumbsUp) {
										// Optimistically update the message in the cache
										updateMessageInConversationCache(
											getConversationChatMessageQueryKey({ id: conversationId, impersonationContext }),
											{
												...message,
												feedback: undefined,
											}
										);
										deleteFeedbackMutation.mutate({
											conversationId,
											messageId: message.id,
											impersonationContext,
										});
									} else {
										// Optimistically update the message in the cache
										updateMessageInConversationCache(
											getConversationChatMessageQueryKey({ id: conversationId, impersonationContext }),
											{
												...message,
												// @ts-ignore
												feedback: {
													value: Api.AIChatMessageFeedback.ThumbsUp,
												},
											}
										);
										feedbackMutation.mutate({
											conversationId,
											messageId: message.id,
											feedbackType: Api.AIChatMessageFeedback.ThumbsUp,
											impersonationContext,
										});
									}
								}}
							>
								<ThumbsUpIcon />
							</MessageAction>
							<MessageAction
								tooltip={
									message.feedback?.value === Api.AIChatMessageFeedback.ThumbsDown ? 'Remove feedback' : 'Bad response'
								}
								variant='ghost'
								styles={[message.feedback?.value === Api.AIChatMessageFeedback.ThumbsDown && s.messageActionToggled]}
								onClick={() => {
									if (feedbackMutation.isPending || deleteFeedbackMutation.isPending) return;
									if (message.feedback?.value === Api.AIChatMessageFeedback.ThumbsDown) {
										// Optimistically update the message in the cache
										updateMessageInConversationCache(
											getConversationChatMessageQueryKey({ id: conversationId, impersonationContext }),
											{
												...message,
												feedback: undefined,
											}
										);
										deleteFeedbackMutation.mutate({
											conversationId,
											messageId: message.id,
											impersonationContext,
										});
									} else {
										// Optimistically update the message in the cache
										updateMessageInConversationCache(
											getConversationChatMessageQueryKey({ id: conversationId, impersonationContext }),
											{
												...message,
												// @ts-ignore
												feedback: {
													value: Api.AIChatMessageFeedback.ThumbsDown,
												},
											}
										);
										feedbackMutation.mutate({
											conversationId,
											messageId: message.id,
											feedbackType: Api.AIChatMessageFeedback.ThumbsDown,
											impersonationContext,
										});
									}
								}}
							>
								<ThumbsDownIcon />
							</MessageAction>
						</MessageActions>
					</MessageToolbar>
				) : undefined}
			</AIChatAssistantMessage>
		);
	}
	if ((message as AIChatMessageError).error) {
		const errorMessage = message as AIChatMessageError;
		return <AIChatAssistantMessageError onTryAgain={() => onErrorTryAgain?.(errorMessage.chatRequest)} />;
	}

	return null;
}

const s = StyleSheet.create({
	messageActionToggled: {
		color: darkGrayFontColor,
		':has(*) > svg': {
			fill: darkGrayFontColor,
		},
		':has(*) > svg > path:last-child': {
			color: white,
		},
	},
	useEmailBtn: {
		width: `100%`,
	},
	calendarEntry: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.xs}px ${THEME.spacing.sm}px`,
		borderRadius: THEME.common.borderRadius,
		border: THEME.common.border,
		backgroundColor: THEME.colors.background.main,
		cursor: 'pointer',
		marginBottom: THEME.spacing.xs,
	},
	calendarEntryCheckbox: {
		marginTop: THEME.spacing.sm,
	},
	calendarEntryInfo: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	calendarEntryText: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		lineHeight: 1.5,
	},
	calendarEntrySource: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		lineHeight: 1.5,
	},
	calendarMonthHeader: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.light,
		textTransform: 'uppercase',
		marginBottom: THEME.spacing.xs,
	},
	calendarMonthHeaderSpaced: {
		marginTop: THEME.spacing.md,
	},
	calendarEntryIcon: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
		marginTop: THEME.spacing.sm,
	},
	calendarEntryPreviewBtn: {
		flexShrink: 0,
	},
	calendarEntryDay: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		flexShrink: 0,
	},
	calendarSelectAllRow: {
		display: 'flex',
		justifyContent: 'flex-end',
	},
	assistantEmail: {
		display: `flex`,
		flexDirection: `column`,
		gap: 16,
		fontWeight: `normal`,
	},
	attachmentImage: {
		maxWidth: 200,
		maxHeight: 200,
		borderRadius: THEME.common.borderRadius,
		border: THEME.common.border,
		objectFit: 'contain',
	},
	messageActions: {
		display: 'flex',
		alignItems: 'center',
	},
	messageToolbar: {
		display: 'flex',
		width: '100%',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
	},
	tooltipText: {
		padding: 0,
		margin: 0,
	},
	srOnly: {
		position: 'absolute',
		width: 1,
		height: 1,
		padding: 0,
		margin: -1,
		overflow: 'hidden',
		clip: 'rect(0, 0, 0, 0)',
		whiteSpace: 'nowrap',
		borderWidth: 0,
	},
});
